import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { trialUrgency } from '@tailoring-crm/shared';
import { useAuth } from '../../lib/auth/auth-context.js';
import { useBillingState } from '../../lib/billing/use-billing.js';
import { cn } from '@tailoring-crm/ui';

/**
 * The trial clock, shown where the owner already is rather than on a screen they must go to.
 *
 * It escalates and it is quiet until it needs not to be. Eleven days out it is a grey line at
 * the top of the shell; three days out it is amber; on the last day it is red. Showing the same
 * red bar for a fortnight is how a product teaches somebody to stop seeing it, and then the one
 * day it mattered is the day they miss.
 *
 * It renders for the OWNER only. A karigar has no say in what the shop pays for and cannot act
 * on it, so a countdown they cannot do anything about is just anxiety.
 */
export function TrialBanner(): ReactElement | null {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isOwner = role === 'owner';
  const billing = useBillingState({ enabled: isOwner });

  const state = billing.data;
  if (!isOwner || !state) return null;
  if (state.status !== 'trialing' || state.trialDaysRemaining === null) return null;

  const urgency = trialUrgency(state.trialDaysRemaining);
  // Eleven days of "you have eleven days left" is a nag. The countdown starts speaking at seven.
  if (urgency === 'calm' && state.trialDaysRemaining > 7) return null;

  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm',
        urgency === 'urgent'
          ? 'border-destructive/40 bg-destructive/5'
          : urgency === 'soon'
            ? 'border-warning/40 bg-warning/5'
            : 'border-border bg-muted/40',
      )}
    >
      <span className="flex items-center gap-2">
        <Clock aria-hidden="true" className="size-4" />
        {state.trialDaysRemaining === 0
          ? t('billing.trialEndsToday')
          : t('billing.trialRemaining', { count: state.trialDaysRemaining })}
      </span>
      <Link
        to="/settings/billing"
        className="font-medium text-primary underline underline-offset-2"
      >
        {t('billing.subscribe')}
      </Link>
    </div>
  );
}
